import { useEffect, useState } from "react";
import { toast } from "sonner";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useCan } from "@/lib/auth";

import {
  useFeedDefinitionChanges,
  usePatchFeedDefinition,
  type FeedDefinitionChangeOut,
  type FeedDefinitionOut,
} from "../api";

const CADENCES = ["continuous", "hourly", "daily", "weekly", "irregular", "unknown"];
const GRAINS = ["integration", "company", "location", "unknown"];
const INTERVAL_BY_CADENCE: Record<string, number | null> = {
  continuous: 4,
  hourly: 2,
  daily: 24,
  weekly: 168,
  irregular: null,
  unknown: null,
};

/** The feed-definition record (jrae 2026-08-20 post-launch): click a row in
 *  Admin and everything about HOW this integration delivers is editable here
 *  (admin-gated), with the changelog — the written record of how our
 *  understanding of the integration changed — underneath. Quick view edits;
 *  expanded adds the full trail. Credential EXPIRY belongs here too, but the
 *  warehouse strips credential payloads (ENTITY_DATA is empty, ENTITY_SOURCE
 *  null on every INTEGRATION_CREDENTIAL row), so it waits on an engine API
 *  (COPS-3 follow-up with Cinch eng). */
export function FeedDefinitionDrawer({
  definition,
  expanded,
  onExpandedChange,
  onClose,
}: {
  definition: FeedDefinitionOut | null;
  expanded: boolean;
  onExpandedChange: (v: boolean) => void;
  onClose: () => void;
}) {
  const canAdmin = useCan("integrations.admin");
  const patch = usePatchFeedDefinition();
  const changes = useFeedDefinitionChanges(definition?.id ?? null);
  const [notesDraft, setNotesDraft] = useState("");

  useEffect(() => {
    setNotesDraft(definition?.notes ?? "");
  }, [definition?.id, definition?.notes]);

  if (definition == null) return null;
  const d = definition;

  const save = (body: Partial<FeedDefinitionOut>) =>
    patch.mutate(
      { id: d.id, ...body },
      { onSuccess: () => toast.success("Definition updated") },
    );

  const changeColumns: DataTableColumn<FeedDefinitionChangeOut>[] = [
    {
      key: "created_at",
      header: "When",
      render: (r) => new Date(r.created_at).toLocaleString(),
    },
    { key: "field", header: "Field" },
    {
      key: "old_value",
      header: "From",
      render: (r) => <span className="text-muted-foreground">{r.old_value ?? "—"}</span>,
    },
    { key: "new_value", header: "To", render: (r) => r.new_value ?? "—" },
    {
      key: "changed_by_name",
      header: "By",
      render: (r) => r.changed_by_name ?? "nightly sync",
    },
  ];

  const changeRows = changes.data ?? [];

  return (
    <EntityDrawer
      open
      onOpenChange={(v) => {
        if (!v && !expanded) onClose();
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      onClose={onClose}
      title={d.entity_source}
      subtitle={`Feed definition — ${d.delivery_mode}`}
    >
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-2">
          <StatusBadge
            value={d.status === "confirmed" ? "approved" : "pending"}
            label={d.status}
          />
          {d.suggested_from && (
            <span className="text-xs text-muted-foreground">
              suggested from {d.suggested_from}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Cadence">
            {canAdmin ? (
              <Select
                value={d.cadence}
                onValueChange={(v) =>
                  save({ cadence: v, expected_interval_h: INTERVAL_BY_CADENCE[v] })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CADENCES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-sm">{d.cadence}</span>
            )}
          </Field>
          <Field label="Expect every">
            <span className="text-sm">
              {d.expected_interval_h != null ? `${d.expected_interval_h}h` : "learned"}
            </span>
          </Field>
          <Field label="Credentials at">
            {canAdmin ? (
              <Select
                value={d.credential_grain}
                onValueChange={(v) => save({ credential_grain: v })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRAINS.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-sm">{d.credential_grain}</span>
            )}
          </Field>
          <Field label="Data at">
            {canAdmin ? (
              <Select value={d.data_grain} onValueChange={(v) => save({ data_grain: v })}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRAINS.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-sm">{d.data_grain}</span>
            )}
          </Field>
          <Field label="Watch companies (credential sweeps)">
            <Switch
              checked={d.monitor_l2_outages}
              disabled={!canAdmin}
              onCheckedChange={(v) => save({ monitor_l2_outages: v })}
            />
          </Field>
          <Field label="Watch locations (file sweeps)">
            <Switch
              checked={d.monitor_l3_outages}
              disabled={!canAdmin}
              onCheckedChange={(v) => save({ monitor_l3_outages: v })}
            />
          </Field>
        </div>

        <p className="text-xs text-muted-foreground">
          Credential expiry isn&apos;t tracked yet — the warehouse strips credential
          payloads, so surfacing &quot;expires soon&quot; waits on an engine API
          (follow-up open with Cinch eng).
        </p>

        <div>
          <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Notes — how this integration works
          </h3>
          <p className="mb-2 text-xs text-muted-foreground">
            What you record here feeds the digest (and the upcoming AI triage) when
            this feed has trouble — write it for the person on call.
          </p>
          <Textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            disabled={!canAdmin}
            rows={4}
            placeholder="e.g. Files land hourly via partner SFTP; contact is X; a quiet Monday is normal for golf courses."
          />
          {canAdmin && (
            <Button
              size="sm"
              className="mt-2"
              disabled={patch.isPending || notesDraft === (d.notes ?? "")}
              onClick={() => save({ notes: notesDraft })}
            >
              Save notes
            </Button>
          )}
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Change log
          </h3>
          <DataTable
            columns={changeColumns}
            rows={expanded ? changeRows : changeRows.slice(0, 8)}
            rowKey={(r) => r.id}
            loading={changes.isLoading}
            emptyMessage="No changes recorded yet."
            containerClassName={expanded ? "max-h-[32rem]" : "max-h-64"}
          />
          {!expanded && changeRows.length > 8 && (
            <p className="mt-1 text-xs text-muted-foreground">
              Showing the last 8 — expand the drawer for the full trail (
              {changeRows.length}).
            </p>
          )}
        </div>
      </div>
    </EntityDrawer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs text-muted-foreground">{label}</p>
      {children}
    </div>
  );
}
